import {
  AgxButton,
  AgxColumn,
  AgxHeader,
  AgxResponsiveTable,
  AgxRow,
  AgxToast,
  Images,
} from '@urbanx/agx-ui-components'
import { AgxToastState } from 'types/commonTypes'
import './agencySuppliers.scss'
import SupplierUpdateModal from './SupplierUpdateModal'
import { SetStateAction, useState } from 'react'
import useAgencySupplierTableConfig from './useAgencySupplierTableConfig'
import {
  AgencySupplier,
  AgencySuppliers,
  AgencySuppliers as AgencySuppliersType,
  useDeleteAgencySupplier,
  useUpdateAgencySuppliers,
} from '@urbanx/agx.agencies.client'

interface AgencySuppliersProps {
  agencySuppliers?: AgencySuppliersType
  onBack: () => void
}

const AgxAgencySuppliers = ({
  agencySuppliers,
  onBack,
}: AgencySuppliersProps) => {
  if (!agencySuppliers) return null
  const { agencyId } = agencySuppliers
  const successToast: SetStateAction<AgxToastState> = {
    color: 'success',
    message: 'Agency suppliers updated successfully',
    open: true,
  }
  const errorToast: SetStateAction<AgxToastState> = {
    color: 'error',
    message: 'Error updating agency suppliers',
    open: true,
  }
  const [toastState, updateToastState] = useState({
    ...successToast,
    open: false,
  })
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [isEditMode, setEditMode] = useState(false)
  const [currentSuppliers, setCurrentSuppliers] = useState(
    agencySuppliers.suppliers ?? []
  )
  const [selectedSupplierIndex, setSelectedSupplierIndex] = useState(-1)
  const defaultSupplier = {
    supplierId: undefined,
    companyName: undefined,
    email: undefined,
    phone: undefined,
    notes: undefined,
  }

  const { updateAgencySuppliers } = useUpdateAgencySuppliers({
    onSuccess: (updatedSuppliers?: AgencySuppliers) => {
      setShowUpdateModal(false)
      setEditMode(false)
      if (updatedSuppliers?.suppliers?.length) {
        setCurrentSuppliers(updatedSuppliers.suppliers)
      }
      updateToastState(successToast)
    },
    onError: () => updateToastState(errorToast),
  })

  const { deleteAgencySupplier } = useDeleteAgencySupplier({
    onSuccess: (updatedSuppliers?: AgencySuppliers) => {
      setShowUpdateModal(false)
      setEditMode(false)
      if (updatedSuppliers?.suppliers)
        setCurrentSuppliers(updatedSuppliers.suppliers)

      updateToastState(successToast)
    },
    onError: () => updateToastState(errorToast),
  })

  const addSupplierClick = () => {
    setSelectedSupplierIndex(-1)
    setShowUpdateModal(true)
    setEditMode(false)
  }

  const editSupplierClick = (index: number) => {
    setSelectedSupplierIndex(index)
    setShowUpdateModal(true)
    setEditMode(true)
  }

  const updateModal = (
    <SupplierUpdateModal
      isEditMode={isEditMode}
      updateableData={
        isEditMode ? currentSuppliers[selectedSupplierIndex] : defaultSupplier
      }
      onSave={(supplier: AgencySupplier) => {
        updateAgencySuppliers({
          ...supplier,
          agencyId,
        })
      }}
      onDelete={(supplierId: string | null) =>
        deleteAgencySupplier({
          agencyId,
          supplierId: supplierId ?? undefined,
        })
      }
      onClose={() => setShowUpdateModal(false)}
    />
  )

  const [supplierTableConfig, supplierRows] = useAgencySupplierTableConfig(
    currentSuppliers,
    editSupplierClick
  )

  return (
    <AgxColumn fill>
      <AgxRow spaceBetween centered extraClasses={'suppliers-header'}>
        <span className="suppliers-backarrow" onClick={onBack}>
          <Images.ArrowBackOutline />
        </span>
        <AgxHeader size={5}>{'Suppliers'}</AgxHeader>
        <AgxButton
          primary
          small
          text="Add Supplier"
          onClick={addSupplierClick}
        />
      </AgxRow>
      <AgxToast selector="#agxToast" toastState={toastState} />
      <AgxResponsiveTable
        key="agency-suppliers-table"
        columnConfig={supplierTableConfig}
        rowProps={supplierRows}
        showModal={showUpdateModal}
        updateModalComponent={updateModal}
      />
    </AgxColumn>
  )
}

export default AgxAgencySuppliers
